import React, { useRef, useImperativeHandle, forwardRef, type Ref } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type VisibilityState,
  type Table,
  type Row,
  type OnChangeFn,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DataTablePagination } from './DataTablePagination';

/** Virtualization kicks in above this row count */
const VIRTUAL_THRESHOLD = 100;
const ROW_HEIGHT = 37;
const MAX_VIRTUAL_HEIGHT = 600;

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  /** Server-side pagination */
  pageCount?: number;
  pageIndex?: number;
  pageSize?: number;
  onPaginationChange?: (page: number) => void;
  /** Server-side sorting */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Row selection */
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  /** Column visibility */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  /** Toolbar rendered above the table — receives the table instance */
  toolbar?: (table: Table<TData>) => React.ReactNode;
  /** Enable virtual scrolling for large datasets (auto-activates when rows > 100) */
  enableVirtualization?: boolean;
  /** Row styling */
  rowClassName?: (row: Row<TData>) => string;
  className?: string;
}

export interface DataTableRef<TData> {
  table: Table<TData>;
}

function DataTableInner<TData>(
  props: DataTableProps<TData>,
  ref: Ref<DataTableRef<TData>>,
) {
  const {
    columns,
    data,
    pageCount = 1,
    pageIndex = 0,
    pageSize = 50,
    onPaginationChange,
    sorting = [],
    onSortingChange,
    enableRowSelection = false,
    rowSelection = {},
    onRowSelectionChange,
    columnVisibility = {},
    onColumnVisibilityChange,
    toolbar,
    enableVirtualization = false,
    rowClassName,
    className,
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    if (!onSortingChange) return;
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    onSortingChange(next);
  };

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    if (!onRowSelectionChange) return;
    const next = typeof updater === 'function' ? updater(rowSelection) : updater;
    onRowSelectionChange(next);
  };

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    if (!onColumnVisibilityChange) return;
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    onColumnVisibilityChange(next);
  };

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableRowSelection,
    enableMultiSort: false,
    // Cycle between asc and desc only — never the "cleared" state. Without this
    // TanStack's default toggle goes asc → desc → cleared, our handler ignores
    // the cleared payload, and the next click computes against stale state and
    // appears to do nothing (the "click fast or it won't flip" bug).
    enableSortingRemoval: false,
  });

  const rows = table.getRowModel().rows;
  const shouldVirtualize = enableVirtualization && rows.length > VIRTUAL_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? rows.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  useImperativeHandle(ref, () => ({ table }), [table]);

  const renderRow = (row: Row<TData>) => (
    <tr
      key={row.id}
      className={`hover:bg-mono-800/30 transition-colors${
        rowClassName ? ' ' + rowClassName(row) : ''
      }`}
    >
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id} className="px-3 py-2 text-xs text-mono-100">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );

  return (
    <div className={className}>
      {toolbar && toolbar(table)}
      <div
        ref={scrollContainerRef}
        className="border border-mono-800 rounded overflow-x-auto"
        style={shouldVirtualize ? { maxHeight: MAX_VIRTUAL_HEIGHT, overflowY: 'auto' } : undefined}
      >
        <table className="w-full">
          <thead className={shouldVirtualize ? 'sticky top-0 z-10' : undefined}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-mono-800 bg-mono-900/50"
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="px-3 py-2.5 text-left text-xs text-mono-300 tracking-widest font-medium"
                    style={header.column.getCanSort() ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() && (
                      <span className="ml-1">
                        {header.column.getIsSorted() === 'asc' ? '\u25B2' : '\u25BC'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-mono-800/50">
            {shouldVirtualize ? (
              <>
                {virtualizer.getVirtualItems().length > 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ height: virtualizer.getVirtualItems()[0].start, padding: 0, border: 'none' }}
                    />
                  </tr>
                )}
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  return renderRow(row);
                })}
                {virtualizer.getVirtualItems().length > 0 && (() => {
                  const items = virtualizer.getVirtualItems();
                  const lastItem = items[items.length - 1];
                  return (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{
                          height: virtualizer.getTotalSize() - (lastItem?.end ?? 0),
                          padding: 0,
                          border: 'none',
                        }}
                      />
                    </tr>
                  );
                })()}
              </>
            ) : (
              <>
                {rows.map(renderRow)}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-3 py-8 text-center text-xs text-mono-400"
                    >
                      No results.
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && onPaginationChange && (
        <DataTablePagination
          page={pageIndex + 1}
          totalPages={pageCount}
          onPageChange={(p) => onPaginationChange(p - 1)}
        />
      )}
    </div>
  );
}

// forwardRef does not directly support generics, so we cast
export const DataTable = forwardRef(DataTableInner) as <TData>(
  props: DataTableProps<TData> & { ref?: Ref<DataTableRef<TData>> },
) => ReturnType<typeof DataTableInner>;
